import { useState, useRef, useEffect, ComponentPropsWithoutRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/utils/lib/utils';
import { Input } from 'alurkerja-ui';

interface MarkdownInputProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const MarkdownParagraph = (props: ComponentPropsWithoutRef<'p'>) => <p {...props} className="text-gray-800 text-sm leading-relaxed" />;
const MarkdownStrong = (props: ComponentPropsWithoutRef<'strong'>) => <strong {...props} className="font-semibold text-sm text-gray-900" />;
const MarkdownEm = (props: ComponentPropsWithoutRef<'em'>) => <em {...props} className="italic text-sm text-gray-700" />;
const MarkdownCode = (props: ComponentPropsWithoutRef<'code'>) => <code {...props} className="bg-gray-100 rounded px-1 py-0.5 text-sm text-gray-700" />;

const markdownComponents = { p: MarkdownParagraph, strong: MarkdownStrong, em: MarkdownEm, code: MarkdownCode };

export function MarkdownInput({
  value = '',
  onChange,
  placeholder,
  className,
  disabled,
}: Readonly<MarkdownInputProps>) {
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useEffect(() => {
    if (isEditing) adjustHeight();
  }, [value, isEditing]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsEditing(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sharedClasses = cn(
    'w-full min-h-[40px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm transition-all whitespace-pre-wrap break-words',
    disabled && 'cursor-not-allowed opacity-50 bg-gray-100',
    className,
  );

  return (
    <div ref={wrapperRef} className="relative">
      {isEditing ? (
        <Input
          className="text-sm"
          value={value}
          onChange={e => {
            onChange?.(e.target.value);
            adjustHeight();
          }}
          onFocus={adjustHeight}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus
        />
      ) : (
        <div className={cn(sharedClasses, 'cursor-text')} onClick={() => !disabled && setIsEditing(true)}>
          {value ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {value}
            </ReactMarkdown>
          ) : (
            <p className="text-gray-400">{placeholder}</p>
          )}
        </div>
      )}

      {isEditing && (
        <small className="text-gray-500">Supports **bold**, _italic_, ~strikethrough~, ```monoscape```</small>
      )}
      {/* <small className="text-gray-500">Supports **bold**, _italic_, ~strikethrough~, ```monoscape```</small> */}
    </div>
  );
}
